/**
 * EST3 WBS import client preview parser.
 */
import {
  type Est3WbsImportPreset,
  normalizeWbsCode,
  parentCodeFromOutline,
  resolveWbsImportColumns,
} from './est3WbsImportMapping';

export type { Est3WbsImportPreset } from './est3WbsImportMapping';
export {
  detectWbsImportPreset,
  WBS_IMPORT_PRESET_HINTS,
  WBS_IMPORT_PRESET_LABELS,
} from './est3WbsImportMapping';

export interface Est3WbsImportPreviewRow {
  key: number;
  row: number;
  code: string;
  name: string;
  parentCode?: string;
  sortOrder?: number;
  status: 'valid' | 'error';
  errorMessage?: string;
  detectedPreset?: string;
}

export const EST3_WBS_IMPORT_TEMPLATE_HINT = `Code,Name,Parent Code,Sort Order,Layer,Description,Short Name,Reference Scope
0,Sample EPC Project,,10,PROJECT,Entire project scope (Level 0),Project,REF-PROJECT
U01,Crude Distillation Unit,0,20,UNIT,Major operating subdivision,CDU,REF-CDU
U01-A1,Foundation Area,U01,30,AREA,Construction Work Area — foundations and earthworks,Foundations,REF-CWA-FOUND
U01-A1-B01,Equipment Foundations,U01-A1,40,BLOCK,Distinct structure — major equipment foundations,Equip Fdn,
U01-A1-B01-M01,Reactor Foundation Module,U01-A1-B01,50,MODULE,Fabricatable / independently executable chunk,Reactor Fdn,
U01-A1-B01-M01-P01,Rebar & Formwork Install,U01-A1-B01-M01,60,PACKAGE,Lowest executable work package (CWP),Rebar,
U01-A2,Main Process Area,U01,70,AREA,Construction Work Area — main process equipment,Process,REF-CWA-PROC
U01-A2-B01,Pipe Rack Structure,U01-A2,80,BLOCK,Distinct structure — pipe rack,Pipe Rack,
U01-A2-B01-M01,Pipe Rack East Bay,U01-A2-B01,90,MODULE,Module — east bay section,East Bay,
U01-A2-B01-M01-P01,Structural Steel Erection,U01-A2-B01-M01,100,PACKAGE,Construction Work Package — steel erection,Steel,
U01-A2-B01-M02,Pipe Rack West Bay,U01-A2-B01,110,MODULE,Module — west bay section,West Bay,
U01-A2-B01-M02-P01,Piping Installation,U01-A2-B01-M02,120,PACKAGE,Construction Work Package — piping install,Piping,
U01-A3,Utilities Area,U01,130,AREA,Construction Work Area — utilities,Utilities,REF-CWA-UTIL
U01-A3-B01,Cooling Water Pump Station,U01-A3,140,BLOCK,Distinct structure — CW pump station,CW Pump,
U01-A3-B01-M01,CW Pump Skid,U01-A3-B01,150,MODULE,Skidded pump package module,Pump Skid,
U01-A3-B01-M01-P01,Skid Hook-up,U01-A3-B01-M01,160,PACKAGE,Construction Work Package — skid hook-up,Hook-up,
U02,Utilities & Offsites,0,170,UNIT,Major subdivision — offsites and utilities,Offsites,REF-OFFSITES
U02-A1,Substation Area,U02,180,AREA,Construction Work Area — electrical substation,Substation,REF-CWA-SUB
U02-A1-B01,Control Building,U02-A1,190,BLOCK,Distinct structure — control building,Ctrl Bldg,
U02-A1-B01-M01,Electrical Room,U02-A1-B01,200,MODULE,Module — electrical room fit-out,Elec Room,
U02-A1-B01-M01-P01,Cable Tray Installation,U02-A1-B01-M01,210,PACKAGE,Construction Work Package — cable trays,Cable Tray,
`;

function cellStr(row: unknown[], idx: number): string {
  if (idx < 0) return '';
  const v = row[idx];
  return v != null ? String(v).trim() : '';
}

function cellNum(row: unknown[], idx: number): number {
  const s = cellStr(row, idx);
  if (!s) return NaN;
  return Number(s.replace(/,/g, ''));
}

export function parseEst3WbsSheet(
  aoa: unknown[][],
  preset: Est3WbsImportPreset = 'auto'
): Est3WbsImportPreviewRow[] {
  if (!aoa.length) return [];
  const headerRow = (aoa[0] ?? []).map((h) => String(h ?? ''));
  const cols = resolveWbsImportColumns(headerRow, preset);

  if (cols.code < 0 || cols.name < 0) {
    return [
      {
        key: 1,
        row: 1,
        code: '',
        name: '',
        status: 'error',
        errorMessage: `Missing required columns for ${cols.preset} format`,
        detectedPreset: cols.preset,
      },
    ];
  }

  const out: Est3WbsImportPreviewRow[] = [];
  for (let r = 1; r < aoa.length; r++) {
    const row = aoa[r] as unknown[];
    if (!row?.some((c) => String(c ?? '').trim())) continue;

    const code = normalizeWbsCode(cellStr(row, cols.code));
    const name = cellStr(row, cols.name);
    let parentCode = cols.parentCode >= 0 ? cellStr(row, cols.parentCode) || undefined : undefined;
    if (!parentCode && cols.preset === 'msproject' && code) {
      parentCode = parentCodeFromOutline(code);
    }
    const sortN = cols.sortOrder >= 0 ? cellNum(row, cols.sortOrder) : r * 10;
    const outlineLevel = cols.outlineLevel >= 0 ? cellNum(row, cols.outlineLevel) : NaN;

    if (!code && !name) continue;
    if (cols.preset === 'msproject' && outlineLevel === 0) continue;

    let status: 'valid' | 'error' = 'valid';
    let errorMessage: string | undefined;
    if (!code) {
      status = 'error';
      errorMessage = 'Code / Outline Number required';
    } else if (!name) {
      status = 'error';
      errorMessage = 'Name / Task Name required';
    } else if (parentCode && normalizeWbsCode(parentCode) === code) {
      status = 'error';
      errorMessage = 'Parent cannot equal code';
    }

    out.push({
      key: r,
      row: r + 1,
      code,
      name: name || '(missing)',
      parentCode: parentCode ? normalizeWbsCode(parentCode) : undefined,
      sortOrder: Number.isFinite(sortN) ? sortN : r * 10,
      status,
      errorMessage,
      detectedPreset: cols.preset,
    });
  }
  return out;
}

export async function parseEst3WbsImportFile(
  file: File,
  preset: Est3WbsImportPreset = 'auto'
): Promise<Est3WbsImportPreviewRow[]> {
  const XLSX = await import('xlsx');
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const sheet = wb.Sheets[wb.SheetNames[0]];
  const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' }) as unknown[][];
  return parseEst3WbsSheet(aoa, preset);
}

export function downloadEst3WbsImportCsvTemplate() {
  const blob = new Blob([EST3_WBS_IMPORT_TEMPLATE_HINT], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'est3-wbs-import-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}
